import { useEffect } from "react";
import "./WaterGunAnimation.css";

export default function WaterGunAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest(".battle-bg");
    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();

    const startX = (pRect.left + pRect.width / 2) - cRect.left;
    const startY = (pRect.top + pRect.height / 2) - cRect.top;
    const endX = (oRect.left + oRect.width / 2) - cRect.left;
    const endY = (oRect.top + oRect.height / 2) - cRect.top;

    const dx = endX - startX;
    const dy = endY - startY;
    const angle = Math.atan2(dy, dx) + "rad";
    
    // Create elements array to track all created elements
    const elements = [];
    
    // Create initial glow effect at source
    const sourceGlow = document.createElement("div");
    sourceGlow.className = "water-source-glow";
    sourceGlow.style.left = `${startX}px`;
    sourceGlow.style.top = `${startY}px`;
    container.appendChild(sourceGlow);
    elements.push(sourceGlow);
    
    // Create water charging effect
    for (let i = 0; i < 12; i++) {
      const chargeParticle = document.createElement("div");
      chargeParticle.className = "water-charging-particle";
      chargeParticle.style.left = `${startX}px`;
      chargeParticle.style.top = `${startY}px`;
      
      const particleAngle = Math.random() * 2 * Math.PI;
      const particleDist = 10 + Math.random() * 15;
      const px = Math.cos(particleAngle) * particleDist;
      const py = Math.sin(particleAngle) * particleDist;
      
      chargeParticle.style.setProperty("--dx", `${px}px`);
      chargeParticle.style.setProperty("--dy", `${py}px`);
      chargeParticle.style.animationDuration = `${300 + Math.random() * 200}ms`;
      
      container.appendChild(chargeParticle);
      elements.push(chargeParticle);
      
      // Auto-remove particles
      setTimeout(() => {
        if (chargeParticle && chargeParticle.parentNode) {
          chargeParticle.remove();
        }
      }, 500);
    }

    // Create water stream effect (instead of a persistent beam)
    const streamTimers = [];
    for (let i = 0; i < 15; i++) {
      const timer = setTimeout(() => {
        // Check if component is still mounted
        if (!container.isConnected) return;
        
        const beam = document.createElement("div");
        beam.className = "water-stream-particle";
        beam.style.left = `${startX}px`;
        beam.style.top = `${startY}px`;
        
        const spreadX = dx + (Math.random() - 0.5) * 12;
        const spreadY = dy + (Math.random() - 0.5) * 12;
        const streamSpeed = 400 + Math.random() * 150;
        
        beam.style.setProperty("--dx", `${spreadX}px`);
        beam.style.setProperty("--dy", `${spreadY}px`);
        beam.style.setProperty("--angle", angle);
        beam.style.setProperty("--speed", `${streamSpeed}ms`);
        
        container.appendChild(beam);
        elements.push(beam);
        
        // Auto-remove particles after animation
        setTimeout(() => {
          if (beam && beam.parentNode) {
            beam.remove();
          }
        }, streamSpeed + 50);
        
        // Add water droplets along the path
        if (i % 3 === 0) {
          for (let j = 0; j < 3; j++) {
            const dropletTimer = setTimeout(() => {
              if (!container.isConnected) return;
              
              const droplet = document.createElement("div");
              droplet.className = "water-droplet";
              
              // Position droplet somewhere along the path
              const progress = 0.2 + Math.random() * 0.6; // Between 20% and 80% along path
              const dropletX = startX + dx * progress;
              const dropletY = startY + dy * progress;
              
              droplet.style.left = `${dropletX}px`;
              droplet.style.top = `${dropletY}px`;
              
              // Random deviation for the droplet
              const dropletAngle = Math.random() * 2 * Math.PI;
              const dropletDist = 5 + Math.random() * 8;
              const tx = Math.cos(dropletAngle) * dropletDist;
              const ty = Math.sin(dropletAngle) * dropletDist;
              
              droplet.style.setProperty("--dx", `${tx}px`);
              droplet.style.setProperty("--dy", `${ty}px`);
              
              container.appendChild(droplet);
              elements.push(droplet);
              
              // Auto-remove droplet
              setTimeout(() => {
                if (droplet && droplet.parentNode) {
                  droplet.remove();
                }
              }, 400);
            }, j * 50);
            streamTimers.push(dropletTimer);
          }
        }
      }, i * 40);
      streamTimers.push(timer);
    }

    const handleImpact = () => {
      // Add splash effect to opponent
      if (opponentEl && opponentEl.classList) {
        opponentEl.classList.add("water-hit");
        setTimeout(() => {
          if (opponentEl && opponentEl.classList) {
            opponentEl.classList.remove("water-hit");
          }
        }, 300);
      }

      // Create impact wave
      const impactWave = document.createElement("div");
      impactWave.className = "water-impact-wave";
      impactWave.style.left = `${endX}px`;
      impactWave.style.top = `${endY}px`;
      container.appendChild(impactWave);
      elements.push(impactWave);
      
      setTimeout(() => {
        if (impactWave && impactWave.parentNode) {
          impactWave.remove();
        }
      }, 500);

      // Create splash particles
      const splashes = [];
      for (let i = 0; i < 25; i++) {
        const splash = document.createElement("div");
        splash.className = "water-splash";
        splash.style.left = `${endX}px`;
        splash.style.top = `${endY}px`;

        const angle = Math.random() * 2 * Math.PI;
        const dist = 15 + Math.random() * 30;
        const tx = Math.cos(angle) * dist;
        const ty = Math.sin(angle) * dist * 0.8 - 5; // Slightly more upward
        
        splash.style.setProperty("--dx", `${tx}px`);
        splash.style.setProperty("--dy", `${ty}px`);
        splash.style.setProperty("--size", `${4 + Math.random() * 8}px`);
        splash.style.setProperty("--delay", `${Math.random() * 150}ms`);
        
        container.appendChild(splash);
        elements.push(splash);
        splashes.push(splash);
      }

      // Create mist effect
      for (let i = 0; i < 8; i++) {
        const mist = document.createElement("div");
        mist.className = "water-impact-mist";
        mist.style.left = `${endX}px`;
        mist.style.top = `${endY}px`;
        
        const mistAngle = Math.random() * 2 * Math.PI;
        const mistDist = 10 + Math.random() * 15;
        const mx = Math.cos(mistAngle) * mistDist;
        const my = Math.sin(mistAngle) * mistDist;
        
        mist.style.setProperty("--dx", `${mx}px`);
        mist.style.setProperty("--dy", `${my}px`);
        mist.style.setProperty("--size", `${20 + Math.random() * 15}px`);
        
        container.appendChild(mist);
        elements.push(mist);
        splashes.push(mist);
      }

      setTimeout(() => {
        splashes.forEach(s => {
          if (s && s.parentNode) {
            s.remove();
          }
        });
        onComplete();
      }, 700);
    };

    // Handle animation end and impact
    const impactTimer = setTimeout(handleImpact, 600);

    // Clean up function
    return () => {
      // Clear all timers
      clearTimeout(impactTimer);
      streamTimers.forEach(timer => clearTimeout(timer));
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

WaterGunAnimation.moveType = "water";
